import type { UseFormReturn } from 'react-hook-form';
import { useCallback } from 'react';
import { faMinus, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Field, Stack, Text, TextInputField } from '@sixui/ui';
import { shapeTokens } from '@sixui/ui/themes/base/shape.stylex';
import { spacingTokens } from '@sixui/ui/themes/base/spacing.stylex';
import stylex from '@stylexjs/stylex';
import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IFormMode } from '~/helpers/types';
import type { IIapPollFormValues } from './IapPollForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { Table } from '~/components/molecules/Table';
import { IapProductSelector } from '~/components/organisms/fields/IapProductSelector';

export type IIapPollFormMainSectionOptionsProps = IContainerProps & {
  form: UseFormReturn<IIapPollFormValues>;
  mode: IFormMode;
};

const styles = stylex.create({
  optionsContainer: {
    paddingBottom: spacingTokens.padding$3,
  },
  options: {
    columnGap: spacingTokens.padding$3,
    rowGap: spacingTokens.padding$1,
    borderRadius: shapeTokens.corner$none,
  },
});

export const IapPollFormMainSectionOptions: React.FC<
  IIapPollFormMainSectionOptionsProps
> = (props) => {
  const { form } = props;
  const { t } = useTranslation();
  const optionsFieldArray = useFieldArray({
    control: form.control,
    name: 'options',
  });

  const handleAddOption = useCallback(() => {
    optionsFieldArray.append({
      optionId: undefined,
      priceId: undefined,
      points: undefined,
      maxVotesPerUserPerArticle: 0,
    });
  }, [optionsFieldArray]);

  return (
    <Stack gap={1}>
      {optionsFieldArray.fields.length ? (
        <ContentBlock>
          <ContentBlock.Body sx={styles.optionsContainer}>
            <Table
              sx={styles.options}
              gridTemplateColumns="repeat(3, 1fr) max-content"
            >
              <Table.Header>
                <Text variant="label" size="lg">
                  {t('IapPollFormMainSectionOptions.fields.priceId.label')}
                </Text>
              </Table.Header>
              <Table.Header>
                <Text variant="label" size="lg">
                  {t('IapPollFormMainSectionOptions.fields.points.label')}
                </Text>
              </Table.Header>
              <Table.Header>
                <Text variant="label" size="lg">
                  {t(
                    'IapPollFormMainSectionOptions.fields.maxVotesPerUserPerArticle.label',
                  )}
                </Text>
              </Table.Header>
              <Table.Header />

              {optionsFieldArray.fields.map((field, index) => (
                <Table.Row key={index} first>
                  <Field
                    as={IapProductSelector}
                    productType="consumable"
                    noSupportingText
                    defaultValue={field.priceId}
                    required
                    onChange={(value?: string) => {
                      optionsFieldArray.update(index, {
                        ...field,
                        priceId: value,
                        ...(value ? { maxVotesPerUserPerArticle: 0 } : {}),
                      });
                    }}
                  />
                  <Field
                    as={TextInputField}
                    type="number"
                    min={0}
                    defaultValue={field.points}
                    required
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      optionsFieldArray.update(index, {
                        ...field,
                        points: Number(event.target.value),
                      });
                    }}
                  />
                  {field.priceId ? (
                    <Field
                      key="maxVotesPerUserPerArticle-disabled"
                      as={TextInputField}
                      type="number"
                      defaultValue={0}
                      disabled
                    />
                  ) : (
                    <Field
                      key="maxVotesPerUserPerArticle-enabled"
                      as={TextInputField}
                      type="number"
                      min={0}
                      defaultValue={field.maxVotesPerUserPerArticle}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        optionsFieldArray.update(index, {
                          ...field,
                          maxVotesPerUserPerArticle: Number(event.target.value),
                        });
                      }}
                    />
                  )}
                  <Button
                    icon={<FontAwesomeIcon icon={faMinus} />}
                    onClick={() => optionsFieldArray.remove(index)}
                    variant="text"
                  >
                    {t('common.remove')}
                  </Button>
                </Table.Row>
              ))}
            </Table>

            <div>
              <Button
                icon={<FontAwesomeIcon icon={faPlus} />}
                onClick={handleAddOption}
                variant="text"
              >
                {t('IapPollFormMainSectionOptions.add')}
              </Button>
            </div>
          </ContentBlock.Body>
        </ContentBlock>
      ) : (
        <div>
          <Button
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={handleAddOption}
            variant="text"
          >
            {t('IapPollFormMainSectionOptions.add')}
          </Button>
        </div>
      )}
    </Stack>
  );
};
